<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'systemDictionaryConfigDialog',
})
</script>
<template>
  <ant-Modal v-model:open="state.dialog.visible" :centered="true" :maskClosable="false" :closable="false"
    :keyboard="false" :title="state.dialog.title" :width="state.dialog.width" :confirm-loading="state.loading.submit"
    :ok-text="state.dialog.submitTxt" @ok="onSubmit" @cancel="closeDialog">
    <div class="form-content">
      <VueDraggable v-if="state.dialog.visible" v-model="tableData" target=".ant-table-tbody" handle=".changeSort"
        :animation="150">
        <a-table :columns="columns" :data-source="tableData" :pagination="false" size="small">
          <template #bodyCell="{ column, record, index }">
            <template v-if="column.key === 'sort'">
              <div class="changeSort" style="cursor: move;">
                <MenuOutlined />
              </div>
            </template>
            <template v-else-if="column.key === 'name'">
              <span v-if="!record.edit">{{ record.name }}</span>
              <a-input v-else v-model:value="record.name" :maxlength="20" size="small" />
            </template>
            <template v-else-if="column.key === 'code'">
              <span v-if="!record.edit">{{ record.code }}</span>
              <a-input v-else v-model:value="record.code" :maxlength="200" size="small" />
            </template>
            <template v-else-if="column.key === 'note'">
              <span v-if="!record.edit">{{ record.note }}</span>
              <a-input v-else v-model:value="record.note" :maxlength="100" size="small" />
            </template>
            <template v-else-if="column.key === 'enabled'">
              <a-switch v-model:checked="record.enabled" :disabled="!record.edit" size="small" />
            </template>
            <template v-else-if="column.key === 'action'">
              <div class="table-action-left">
                <template v-if="!record.edit">
                  <a-button type="link" size="small" class="mr-[4px]"
                    @click="editOrSaveRow(record, 'edit')">编辑</a-button>
                  <a-popconfirm title="确认删除该字典子项？" @confirm="deleteRow(index)">
                    <a-button type="link" size="small" danger>删除</a-button>
                  </a-popconfirm>
                </template>
                <template v-else>
                  <a-button type="link" size="small" @click="editOrSaveRow(record, 'save')">保存</a-button>
                  <a-button type="link" size="small" @click="giveUpRow(record, index)">放弃</a-button>
                </template>
              </div>
            </template>
          </template>
        </a-table>
      </VueDraggable>
      <div class="space-gap" style="height: 8px" />
      <a-button type="link" class="add-item-btn" @click="addItem">
        <PlusOutlined />新增子项
      </a-button>
      <div class="space-gap" style="height: 8px" />
      <div class="space-gap" style="height: 22px" />
    </div>
  </ant-Modal>
</template>

<script setup lang="ts" name="systemDictionaryConfigDialog">
import { reactive, ref, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import { VueDraggable } from 'vue-draggable-plus'
import { MenuOutlined, PlusOutlined } from '@ant-design/icons-vue'
import type { DictionaryConfigTable } from '@/store/system/dictionary'
import { useDictionary } from '@/store/system/dictionary'
const dictionaryStore = useDictionary()

const emit = defineEmits(['updateDictInfo'])

const state = reactive({
  dialog: {
    visible: false,
    title: '',
    submitTxt: '',
    width: '1069px'
  },
  loading: {
    submit: false
  },
  id: ''
})

const tableData = ref<DictionaryConfigTable[]>([])

const columns = [
  { title: '排序', key: 'sort', width: 70, align: 'center' },
  { title: 'ID', dataIndex: 'id', key: 'id', width: 120, ellipsis: true },
  { title: '子项名称', dataIndex: 'name', key: 'name', width: 150, ellipsis: true },
  { title: '数据值', dataIndex: 'code', key: 'code', width: 150, ellipsis: true },
  { title: '描述', dataIndex: 'note', key: 'note', ellipsis: true },
  { title: '启用状态', dataIndex: 'enabled', key: 'enabled', width: 120 },
  { title: '操作', key: 'action', width: 150 }
]

const createEmptyRow = (): DictionaryConfigTable => ({
  id: '',
  name: '',
  code: '',
  note: '',
  sort: 0,
  enabled: true
})

const getRowSnapshot = (row: DictionaryConfigTable) => {
  const { edit, _editSnapshot, ...rest } = row as any
  return JSON.parse(JSON.stringify(rest))
}

const openDialog = (id: string, row: DictionaryConfigTable[]) => {
  state.id = id
  tableData.value = row.map((item: DictionaryConfigTable) => ({
    ...item,
    enabled: item.enabled === '1' || item.enabled === true
  }))
  nextTick(() => {
    state.dialog.title = '字典配置'
    state.dialog.submitTxt = '保存'
    state.dialog.visible = true
  })
}

const onSubmit = async () => {
  const isEdit = tableData.value.some((item) => item.edit)
  if (isEdit) {
    message.warning('请先保存子项数据')
    return
  }
  try {
    state.loading.submit = true
    const dictManageEditList = tableData.value.map((item: DictionaryConfigTable, index: number) => {
      return {
        id: item.id,
        code: item.code,
        name: item.name,
        sort: index + 1,
        note: item.note,
        enabled: item.enabled ? '1' : '0'
      }
    })
    await dictionaryStore.editDict({ pid: state.id, dictManageEditList })
    message.success('字典配置成功')
    state.loading.submit = false
    emit('updateDictInfo')
  } catch (error) {
    state.loading.submit = false
  }
}

const editOrSaveRow = (row: DictionaryConfigTable, type: string) => {
  if (type !== 'edit' && (!row.name || !row.code)) {
    message.warning(!row.name ? '请输入子项名称后再保存' : '请输入数据值后再保存')
    return
  }
  if (type === 'edit') {
    ; (row as any)._editSnapshot = getRowSnapshot(row)
    row.edit = true
    return
  }
  row.edit = false
  delete (row as any)._editSnapshot
}

const giveUpRow = (row: DictionaryConfigTable, index: number) => {
  const source = (row as any)._editSnapshot ?? JSON.parse(JSON.stringify(tableData.value[index]))
  if (!source) return
  Object.assign(row, JSON.parse(JSON.stringify(source)), { edit: false })
  delete (row as any)._editSnapshot
}

const deleteRow = (index: number) => {
  tableData.value.splice(index, 1)
}

const addItem = () => {
  tableData.value.push({
    ...createEmptyRow(),
    edit: true,
    _editSnapshot: createEmptyRow()
  } as any)
}

const closeDialog = () => {
  state.loading.submit = false
  state.dialog.visible = false
}

defineExpose({
  openDialog,
  closeDialog
})
</script>

<style scoped lang="less">
.form-content {
  padding: 10px 15px;
}

.space-gap {
  height: 16px;
}
</style>
